import { Grid2 as Grid, SxProps } from '@mui/material';
import { Button } from '@portals/core/src/components/Button/Button';
import { getFixedT } from 'i18next';
import React, { useCallback, useContext, useState } from 'react';

import { CockpitBox } from '../../../components';
import { SavedSearchContext } from '../../../context';
import { useCockpitAccess } from '../../../hooks';
import { styles } from './SavedSearches.styles';
import { getItemKey } from './SavedSearches.utils';
import { SavedSearchesEmptyItem } from './SavedSearchesEmptyItem/SavedSearchesEmptyItem';
import { SavedSearchItem } from './SavedSearchesItem/SavedSearchItem';

const t = getFixedT(null, 'core-immobilien');

export type SavedSearchesProps = {
  countUnit?: number;
};

export const SavedSearches: React.FC<SavedSearchesProps> = ({ countUnit = 6 }) => {
  const { savedSearches, setSavedSearches } = useContext(SavedSearchContext);
  const [visibleCount, setVisibleCount] = useState(countUnit);
  const visibleSearches = savedSearches?.slice(0, visibleCount) || [];
  const { isCockpitAccessEnabled } = useCockpitAccess();

  const handleDelete = useCallback(
    (itemKey: string) => {
      if (savedSearches) {
        const index = savedSearches.findIndex((item) => item.searchParams && getItemKey(item.searchParams) === itemKey);
        if (index !== -1) {
          const filteredSearches = savedSearches.filter((_, i) => i !== index);
          setSavedSearches(filteredSearches);
          if (index < visibleCount) {
            setVisibleCount((prev) => prev - 1);
          }
        }
      }
    },
    [savedSearches, setSavedSearches, visibleCount]
  );

  const handleShowMore = useCallback(() => {
    setVisibleCount((prev) => prev + countUnit);
  }, [countUnit]);

  return (
    <Grid container spacing={{ xs: 5, lg: 9 }} alignItems="stretch" className="saved-searches container">
      {!savedSearches || savedSearches.length === 0 ? (
        <Grid size={12}>
          <SavedSearchesEmptyItem />
        </Grid>
      ) : (
        <>
          {visibleSearches.map(
            (searchItem, index) =>
              searchItem.searchParams && (
                <Grid key={getItemKey(searchItem.searchParams)} size={{ xs: 12, lg: 4 }} sx={{ order: index * 2 }}>
                  <SavedSearchItem
                    searchParams={searchItem.searchParams}
                    itemKey={getItemKey(searchItem.searchParams)}
                    onDelete={handleDelete}
                  />
                </Grid>
              )
          )}
          {isCockpitAccessEnabled && (
            <Grid size={{ xs: 12, lg: 4 }} sx={{ order: 3 }}>
              <CockpitBox
                title={t('watchlist.cockpit.searches.title')}
                content={t('watchlist.cockpit.searches.content')}
              />
            </Grid>
          )}
          {savedSearches.length > visibleSearches.length && (
            <Grid size={12} sx={styles.showMoreResultsContainer}>
              <Button
                color="secondary"
                fullWidth
                onClick={handleShowMore}
                className="saved-search-button more-results-button"
                sx={{ ...styles.secondaryButton, ...styles.showMoreResultsButton } as SxProps}
              >
                {t('showMoreResults')}
              </Button>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};

SavedSearches.displayName = 'SavedSearches';
